import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import toast from 'react-hot-toast';
import { callBackend, invalidateQueries, useQuery } from '@/lib/api-hooks';
import { desktopInvoke, isDesktopApp } from '@/lib/desktopApp';
import { currentPushSubscription } from '@/lib/push';
import { backendApi } from '@/lib/backend';
import { useStore } from '@/store/useStore';
import type { CrmFeature } from '../../../convex/crmFeatures';
import type { CallerCard, CrmContextData } from './types';

export type CallPop = {
  callId: string;
  number: string;
  /** The name sent with the number by the caller's network or the PBX phone book. */
  callerIdName?: string;
  did?: string;
  extensions: string[];
  card: CallerCard;
  answeredBy?: string;
  ended: boolean;
  receivedAt: number;
};

type Focus = { type: 'contact'; id: string } | { type: 'company'; id: string } | { type: 'newContact'; phone?: string } | null;

type CrmContextValue = {
  /** null when this user has no CRM (module off); undefined while loading. */
  data: CrmContextData | null | undefined;
  hasFeature: (feature: CrmFeature) => boolean;
  pops: CallPop[];
  dismissPop: (callId: string) => void;
  keepPop: (callId: string) => void;
  focus: Focus;
  openContact: (id: string) => void;
  openCompany: (id: string) => void;
  openNewContact: (phone?: string) => void;
  closeFocus: () => void;
  liveConnected: boolean;
};

const CrmContext = createContext<CrmContextValue | null>(null);

const POP_LINGER_AFTER_END_MS = 15_000;

/** Links into the CRM: ?contact=, ?company=, ?newContact=<number>, ?caller=<number>, and Linkus's /caller?number=. */
function linkTarget(pathname: string, search: string) {
  const params = new URLSearchParams(search);
  const caller = params.get('caller') ?? (pathname === '/caller' ? params.get('number') ?? params.get('caller_number') : null);
  if (params.get('contact')) return { type: 'contact' as const, id: params.get('contact')! };
  if (params.get('company')) return { type: 'company' as const, id: params.get('company')! };
  if (params.get('newContact') !== null && pathname !== '/caller') return { type: 'newContact' as const, phone: params.get('newContact') || undefined };
  if (caller !== null) return { type: 'caller' as const, number: caller.trim() };
  return null;
}

/**
 * CRM state for the whole app: which features are on, my preferences, the live event stream
 * (incoming-call pop-ups and "data changed" refreshes), and which contact/company is open.
 */
export function CrmProvider({ children, variant = 'app' }: { children: ReactNode; variant?: 'app' | 'desktopPopup' }) {
  const desktopPopup = variant === 'desktopPopup';
  // In the desktop app the main window leaves pop-ups to the app's own always-on-top pop-up window.
  const inDesktopMainWindow = !desktopPopup && isDesktopApp();
  const data = useQuery<CrmContextData | null>(backendApi.crmSettings.getMyCrmContext, {});
  const [pops, setPops] = useState<CallPop[]>([]);
  const [focus, setFocus] = useState<Focus>(null);
  const [liveConnected, setLiveConnected] = useState(false);
  const kept = useRef(new Set<string>());
  const refreshTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const hasFeature = useCallback((feature: CrmFeature) => Boolean(data?.features?.[feature]), [data]);
  const setCallerExtension = useStore((state) => state.setCallerExtension);
  const savedExtension = data?.prefs.extension;
  useEffect(() => {
    if (savedExtension) setCallerExtension(savedExtension);
  }, [savedExtension, setCallerExtension]);

  const dismissPop = useCallback((callId: string) => {
    kept.current.delete(callId);
    setPops((current) => current.filter((pop) => pop.callId !== callId));
  }, []);

  const keepPop = useCallback((callId: string) => {
    kept.current.add(callId);
  }, []);

  const hasCrm = Boolean(data);
  const popsWanted = Boolean(data?.features.screenPop && data.prefs.screenPopMode !== 'off') && !inDesktopMainWindow;
  const desktopNotifications = Boolean(data?.prefs.desktopNotifications);
  const myExtension = data?.prefs.extension;
  const streamKey = data ? `${data.customerId}:${data.prefs.extension}:${data.prefs.screenPopMode}` : '';

  // With Web Push on, the service worker notifies when no tab is in front; the page must not notify too.
  const pushActive = useRef(false);
  useEffect(() => {
    void currentPushSubscription()
      .then((subscription) => {
        pushActive.current = Boolean(subscription);
      })
      .catch(() => undefined);
  }, [desktopNotifications]);

  // Open a contact from a link: Linkus's popup URL, a notification click, or the desktop app.
  const openLink = useCallback(async (pathname: string, search: string) => {
    const target = linkTarget(pathname, search);
    if (!target) return;
    if (target.type !== 'caller') {
      setFocus(target);
      return;
    }
    if (!target.number) return;
    try {
      const found = (await callBackend(backendApi.crm.findContactByNumber, { number: target.number })) as { contactId: string } | null;
      setFocus(found ? { type: 'contact', id: found.contactId } : { type: 'newContact', phone: target.number });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not look up the caller');
    }
  }, []);

  // The desktop app opens contacts in this window without reloading it once this is set.
  useEffect(() => {
    if (!hasCrm || desktopPopup) return;
    (window as unknown as { __yeastarifyCrmReady?: boolean }).__yeastarifyCrmReady = true;
    return () => {
      (window as unknown as { __yeastarifyCrmReady?: boolean }).__yeastarifyCrmReady = false;
    };
  }, [hasCrm, desktopPopup]);

  const linkHandled = useRef(false);
  useEffect(() => {
    if (!hasCrm || desktopPopup || linkHandled.current) return;
    linkHandled.current = true;
    const { pathname, search } = window.location;
    if (!linkTarget(pathname, search)) return;
    window.history.replaceState(null, '', '/');
    void openLink(pathname, search);
  }, [hasCrm, desktopPopup, openLink]);

  useEffect(() => {
    if (!hasCrm || desktopPopup) return;
    const fromWorker = (event: MessageEvent) => {
      if (event.data?.type === 'yeastarify:open') void openLink('/', String(event.data.search ?? ''));
    };
    const fromDesktopApp = (event: Event) => void openLink('/', String((event as CustomEvent).detail ?? ''));
    navigator.serviceWorker?.addEventListener('message', fromWorker);
    window.addEventListener('yeastarify:open', fromDesktopApp);
    return () => {
      navigator.serviceWorker?.removeEventListener('message', fromWorker);
      window.removeEventListener('yeastarify:open', fromDesktopApp);
    };
  }, [hasCrm, desktopPopup, openLink]);

  useEffect(() => {
    if (!hasCrm || typeof EventSource === 'undefined') return;
    const source = new EventSource(desktopPopup ? '/api/events?client=desktop' : '/api/events', { withCredentials: true });

    const refreshSoon = () => {
      if (refreshTimer.current) return;
      refreshTimer.current = setTimeout(() => {
        refreshTimer.current = null;
        invalidateQueries();
      }, 1500);
    };

    source.addEventListener('ready', () => setLiveConnected(true));
    source.addEventListener('error', () => setLiveConnected(false));
    source.addEventListener('crm.changed', refreshSoon);
    source.addEventListener('task.created', (event) => {
      const task = JSON.parse((event as MessageEvent).data) as { title: string; vip: boolean };
      if (task.vip) toast(`⭐ ${task.title}`, { duration: 8000 });
    });
    source.addEventListener('call.ringing', (event) => {
      if (!popsWanted) return;
      const payload = JSON.parse((event as MessageEvent).data) as Omit<CallPop, 'ended' | 'receivedAt'>;
      setPops((current) => {
        const existing = current.find((pop) => pop.callId === payload.callId);
        if (existing) {
          return current.map((pop) => (pop.callId === payload.callId ? { ...pop, extensions: payload.extensions } : pop));
        }
        return [{ ...payload, ended: false, receivedAt: Date.now() }, ...current].slice(0, 4);
      });
      if (!desktopPopup && !pushActive.current && desktopNotifications && document.hidden && 'Notification' in window && Notification.permission === 'granted') {
        const card = payload.card;
        const idName = payload.callerIdName ?? (card.known ? card.contact.callerIdName : undefined);
        const title =
          card.known && card.contact.kind !== 'caller' ? `${card.contact.name}${card.contact.company ? ` · ${card.contact.company}` : ''}` : idName ?? payload.number;
        new Notification(`Incoming call: ${title}`, {
          body: card.known && card.openTasks.length ? `${card.openTasks.length} open task(s)` : card.known ? '' : 'Unknown caller',
          tag: payload.callId,
        });
      }
    });
    source.addEventListener('call.answered', (event) => {
      const { callId, extension } = JSON.parse((event as MessageEvent).data) as { callId: string; extension: string };
      setPops((current) => current.map((pop) => (pop.callId === callId ? { ...pop, answeredBy: extension } : pop)));
      // Someone else picked up: my pop-up has done its job unless I'm working in it.
      if (extension !== myExtension) {
        setTimeout(() => {
          if (!kept.current.has(callId)) setPops((current) => current.filter((pop) => pop.callId !== callId));
        }, 4000);
      }
    });
    source.addEventListener('call.ended', (event) => {
      const { callId } = JSON.parse((event as MessageEvent).data) as { callId: string };
      setPops((current) => current.map((pop) => (pop.callId === callId ? { ...pop, ended: true } : pop)));
      setTimeout(() => {
        if (!kept.current.has(callId)) setPops((current) => current.filter((pop) => pop.callId !== callId));
      }, POP_LINGER_AFTER_END_MS);
    });

    return () => {
      source.close();
      setLiveConnected(false);
      if (refreshTimer.current) clearTimeout(refreshTimer.current);
      refreshTimer.current = null;
    };
    // streamKey: reconnect when my extension or pop-up mode changes, so the server routes pop-ups to me correctly.
  }, [hasCrm, streamKey, popsWanted, desktopNotifications, myExtension, desktopPopup]);

  const value = useMemo<CrmContextValue>(
    () => ({
      data,
      hasFeature,
      pops,
      dismissPop,
      keepPop,
      focus,
      // The desktop pop-up window is too small for panels: it hands them to the app's main window.
      openContact: (id) => (desktopPopup ? void desktopInvoke('open_main', { search: `?contact=${encodeURIComponent(id)}` }) : setFocus({ type: 'contact', id })),
      openCompany: (id) => (desktopPopup ? void desktopInvoke('open_main', { search: `?company=${encodeURIComponent(id)}` }) : setFocus({ type: 'company', id })),
      openNewContact: (phone) =>
        desktopPopup ? void desktopInvoke('open_main', { search: `?newContact=${encodeURIComponent(phone ?? '')}` }) : setFocus({ type: 'newContact', phone }),
      closeFocus: () => setFocus(null),
      liveConnected,
    }),
    [data, hasFeature, pops, dismissPop, keepPop, focus, liveConnected, desktopPopup]
  );

  return <CrmContext.Provider value={value}>{children}</CrmContext.Provider>;
}

export function useCrm() {
  const value = useContext(CrmContext);
  if (!value) throw new Error('useCrm must be used inside CrmProvider');
  return value;
}

/** For components that may render outside the CRM provider (e.g. reports in other layouts). */
export function useOptionalCrm() {
  return useContext(CrmContext);
}
